import React, {
	useState,
	useContext,
	useRef,
	useEffect,
	useCallback,
	useMemo,
	useLayoutEffect,
} from 'react';
import { useMutation } from '@apollo/react-hooks';
import { css } from '@compiled/react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { AnnotationInfo } from '@atlaskit/editor-plugin-annotation';
import Button from '@atlaskit/button/new';

import { CommentEditor } from '@confluence/comment';
import { NewReplyContainer } from '@confluence/inline-comments-common/entry-points/styled';
import type {
	InlineCommentReply,
	NewReplyVars,
	CommentLocation,
	CommentReply,
} from '@confluence/inline-comments-queries';
import { CreateInlineReplyMutation } from '@confluence/inline-comments-queries';
import { CreateGeneralReplyMutation } from '@confluence/page-comments-queries/entry-points/CreateGeneralReplyMutation.graphql';
import type {
	CreateGeneralReplyMutationData,
	CreateGeneralReplyMutationVariables,
} from '@confluence/page-comments-queries/entry-points/__types__/CreateGeneralReplyMutation';
import {
	useCommentsData,
	CommentType,
	type CommentData,
	type ReplyData,
} from '@confluence/comments-data';
import {
	ReactionsContext,
	useCommentContentDispatchContext,
	useCommentContentContext,
} from '@confluence/comment-context';
import type { PageMode } from '@confluence/page-utils/entry-points/enums';
import {
	ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { useUnreadComments } from '@confluence/unread-comments';
import type { PageInfoNode } from '@confluence/page-info';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { fg } from '@confluence/feature-gating';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { RepliesWrapper } from '@confluence/inline-comments-common/entry-points/components';
import { useSessionData } from '@confluence/session-data';
import { useSearchSessionId } from '@confluence/search-session';

import { onSaveReply, ViewReplyOptions } from '../helper/commentThreadHelper';
import { useCommentsPanel } from '../hooks/useCommentsPanel';

import { Comment } from './Comment';
import { defaultDisplayCount, RepliesToggler } from './RepliesToggler';
import { COMMENTS_PANEL } from '../helper/panelConsts';

const commentThreadRepliesBoxStyles = css({
	display: 'flex',
	flexDirection: 'column',
});

const replyBoxStyles = css({
	listStyle: 'none',
	position: 'relative',
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.300'),
});

const oldReplyBoxStyles = css({
	listStyle: 'none',
	position: 'relative',
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.200'),
});

const translucentStyle = css({
	opacity: '50%',
});

const dynamicReplyListStyles = css({
	paddingInlineStart: token('space.0'),
	position: 'relative',
	'&::before': {
		content: "''",
		position: 'absolute',
		left: 0,
		borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		top: 0,
		height: `var(--replyBranchHeight)`,
	},
});

const replyListStaticStraightBorders = css({
	paddingInlineStart: token('space.0'),
	borderLeft: `2px solid ${token('color.border')}`,
});

const replyActionButtonStyles = {
	marginLeft: token('space.300'),
	paddingLeft: token('space.100'),
	width: '62px',
	height: '16px',
	left: '-12px',
};

const customizedReplyButtonStyles = css({
	'&::after': {
		borderStyle: 'none',
	},
});

const commentEditorCustomStyles = {
	marginLeft: token('space.300'),
	height: '16px',
};

export type CommentRepliesProps = {
	parentComment: CommentData;
	pageInfo: PageInfoNode | null;
	isCurrentSelectedComment: boolean;
	hoveredCommentId: string;
	pageMode: PageMode;
	editCommentQueryId: string;
	selectedAnnotation: AnnotationInfo;
	parentCommentId: string;
	handleMouseEnter: (id?: string) => void;
	handleMouseLeave: () => void;
};

const i18n = defineMessages({
	replyAction: {
		id: 'comments-panel.reply.action',
		defaultMessage: 'Reply',
		description: 'Reply button label for comment thread in comments panel',
	},
});

export const CommentReplies = ({
	parentComment,
	pageInfo,
	isCurrentSelectedComment,
	hoveredCommentId,
	pageMode,
	editCommentQueryId,
	selectedAnnotation,
	parentCommentId,
	handleMouseEnter,
	handleMouseLeave,
}: CommentRepliesProps) => {
	const [createInlineReplyFn] = useMutation<
		{ replyInlineComment: InlineCommentReply },
		{ input: NewReplyVars }
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		CreateInlineReplyMutation,
	);

	const [createGeneralReplyFn] = useMutation<
		CreateGeneralReplyMutationData,
		CreateGeneralReplyMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		CreateGeneralReplyMutation,
	);

	const { isReactionsEnabled } = useContext(ReactionsContext);

	const { onChange, resetContentChanged } = useCommentContentDispatchContext();

	const { cloudId } = useSessionData();
	const [{ searchSessionId }] = useSearchSessionId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [, { addReplyToCommentThread }] = useCommentsData();
	const [, { updateReadCommentsListState }] = useUnreadComments();
	const [
		{ commentsMarkedAsRead, replyToCommentId },
		{ setCommentsMarkedAsRead, setReplyToCommentId },
	] = useCommentsPanel();

	const commentType = parentComment.type;
	const resolveProperties = (parentComment?.location as CommentLocation)?.inlineResolveProperties;

	const allReplies = useMemo(() => {
		return parentComment.replies.filter((r) => (r as CommentReply) !== null);
	}, [parentComment.replies]);

	const unreadReplies = useMemo(() => {
		return allReplies.filter((r) => r.isUnread);
	}, [allReplies]);

	// we'll show read/unread replies even if they are marked as read
	const [visibleReplies, setVisibleReplies] = useState<ReplyData[]>(
		resolveProperties?.resolved
			? []
			: unreadReplies.slice(-Math.min(unreadReplies.length, defaultDisplayCount)),
	);
	const [, setCommentForEdit] = useState('');

	const [currentReplyView, setReplyView] = useState(ViewReplyOptions.DEFAULT);

	const { hasContentChanged } = useCommentContentContext();
	const { showModal } = useDialogs();

	const experienceTracker = useContext(ExperienceTrackerContext);

	// used for reply branching styling
	const defaultBranchingHeight = 4;
	const replyButtonRef = useRef<HTMLButtonElement | null>(null);
	const replyListRef = useRef<HTMLDivElement | null>(null);
	const [replyBranchHeight, setReplyBranchHeight] = useState(defaultBranchingHeight);

	const pageId = pageInfo?.id ?? '';
	const pageType = pageInfo?.type ?? '';

	// User page permissions
	const operations = pageInfo?.operations || [];

	// The user can upload media only if they have update permissions for the page
	const hasMediaUploadPermissions = operations.some(
		(op) => op?.operation === 'update' && op?.targetType === pageType,
	);

	const isParentCommentOpen = parentComment.isOpen;
	const threadKey =
		parentComment.type === CommentType.INLINE
			? (parentComment?.location as CommentLocation).inlineMarkerRef || ''
			: parentCommentId;
	const supportedTopLevelActions: CommentAction[] = isParentCommentOpen
		? ['edit', 'resolve', 'delete']
		: [];

	const expandReplyEditor = isParentCommentOpen && parentCommentId === replyToCommentId;

	const visibleUnreadRepliesNotMarkedAsRead = useMemo(() => {
		return visibleReplies
			.filter((reply) => {
				const markedReplies = commentsMarkedAsRead[commentType][threadKey];
				// Check if the reply id is not in the markedReplies set
				return !markedReplies || !markedReplies.has(reply.id);
			})
			.map((reply) => reply.id);
	}, [visibleReplies, commentsMarkedAsRead, threadKey, commentType]);

	const calculateBranchHeight = useCallback(() => {
		let branchHeight = defaultBranchingHeight;

		// the branch height should reach the button by default
		if (replyButtonRef.current) {
			branchHeight = replyButtonRef.current.offsetHeight - 28; // Atlaskit has the height of the button as 32px by default
		}

		// if there are visible replies or hidden replies, then add that height as well to the branch height
		if (replyListRef.current) {
			branchHeight += replyListRef.current.offsetHeight;
		}
		return branchHeight;
	}, [defaultBranchingHeight, replyButtonRef, replyListRef]);

	const numOfHiddenReplies = useRef(
		currentReplyView === ViewReplyOptions.ALL ? 0 : allReplies.length - visibleReplies.length,
	);

	// ensures that the DOM measurements and updates occur before the browser paints, making the updates appear synchronous with the rendering of visibleReplies
	useLayoutEffect(() => {
		setReplyBranchHeight(calculateBranchHeight());
	}, [visibleReplies, calculateBranchHeight]);

	// if selecting the comment thread, we need to mark all visible replies as read as well
	// we'll let setCommentsMarkedAsRead handle duplicates if any
	useEffect(() => {
		if (isCurrentSelectedComment) {
			setCommentsMarkedAsRead({
				threadKey,
				commentIds: visibleUnreadRepliesNotMarkedAsRead,
				commentType,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- there might be a better way to handle this without needing a useEFfect
	}, [isCurrentSelectedComment]);

	// handle updates to replies
	useEffect(() => {
		if (currentReplyView === ViewReplyOptions.ALL) {
			setVisibleReplies(allReplies);
			numOfHiddenReplies.current = 0;
		} else {
			const unreadRepliesNotMarkedAsRead = unreadReplies.filter((reply) => {
				const markedReplies = commentsMarkedAsRead[commentType][threadKey];
				// Check if the reply id is not in the markedReplies set
				return !markedReplies || !markedReplies.has(reply.id);
			});

			const unreadRepliesToShow = unreadRepliesNotMarkedAsRead.slice(
				-Math.min(unreadReplies.length, defaultDisplayCount),
			);
			setVisibleReplies(unreadRepliesToShow);
			numOfHiddenReplies.current = allReplies.length - unreadRepliesToShow.length;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allReplies, unreadReplies]);

	const handleReplyButtonClick = () => {
		if (hasContentChanged) {
			// if the content has changed and the user has clicked reply button to another comment thread, warn the user
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					setReplyToCommentId(parentCommentId);
				},
			});
		} else {
			setReplyToCommentId(parentCommentId);
		}
	};

	const renderReplyButton = () => {
		return fg('confluence_frontend_comments_panel_v2') ? (
			<RepliesWrapper
				showDefaultStyles
				customStyles={replyActionButtonStyles}
				testId="comments-panel-reply-comment-action"
				onClick={handleReplyButtonClick}
			>
				<Button
					data-cy="comments-panel-reply-comment-action"
					css={customizedReplyButtonStyles}
					appearance="subtle"
					ref={replyButtonRef}
					isDisabled={!isParentCommentOpen}
					analyticsContext={{
						attributes: {
							name: 'replyToCommentButton',
							source: COMMENTS_PANEL,
						},
					}}
				>
					<FormattedMessage {...i18n.replyAction} />
				</Button>
			</RepliesWrapper>
		) : (
			<></>
		);
	};

	const renderCommentEditor = () => {
		return expandReplyEditor ? (
			<NewReplyContainer
				mode="view"
				isCommentsPanel
				data-testid="comments-panel-new-reply-container"
			>
				<RepliesWrapper
					showDefaultStyles={fg('confluence_frontend_comments_panel_v2')}
					customStyles={
						fg('confluence_frontend_comments_panel_v2') ? commentEditorCustomStyles : {}
					}
				>
					<CommentEditor
						pageId={pageId}
						pageType={pageType}
						appearance="chromeless"
						EditorComponent={ChromelessEditor}
						onSaveComment={(adf, onSuccess) => {
							setReplyView(ViewReplyOptions.ALL);
							setReplyToCommentId();
							resetContentChanged();

							return onSaveReply({
								pageId,
								parentCommentId,
								cloudId,
								adf,
								onSuccess,
								pageMode,
								editCommentQueryId,
								setCommentForEdit,
								createAnalyticsEvent,
								pageType,
								experienceTracker,
								createInlineReplyFn,
								createGeneralReplyFn,
								selectedAnnotation,
								addReplyToCommentThread,
								threadKey,
								updateReadCommentsListState,
								commentType: parentComment.type,
								searchSessionId,
								isReactionsEnabled,
							});
						}}
						onContentChange={onChange}
						onEditorReady={() => {
							experienceTracker.succeed({
								name:
									parentComment.type === CommentType.INLINE
										? REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE
										: ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
							});
						}}
						commentMode="reply"
						commentType="inline"
						spaceId=""
						showCancelButton
						useNewWarningModal
						hideWatchCheckbox
						expandEditor={expandReplyEditor}
						pageMode="view"
						hasMediaUploadPermissions={hasMediaUploadPermissions}
						shouldDisplayCommentReplyPrompts={false}
						topLevelCommentId={parentComment.id}
						shouldWarnOnInternalNavigation
						commentThreadLength={allReplies.length + 1}
						onCancelComment={() => {
							setReplyToCommentId();
						}}
						isCommentsPanel
					/>
				</RepliesWrapper>
			</NewReplyContainer>
		) : (
			<></>
		);
	};

	return (
		<>
			<div
				data-testid={`comment-thread-${parentComment.id}-replies`}
				css={[
					commentThreadRepliesBoxStyles,
					fg('confluence_frontend_comments_panel_v2') && dynamicReplyListStyles,
					parentComment.wasRemovedByAnotherUser && translucentStyle,
				]}
				style={{ '--replyBranchHeight': `${replyBranchHeight}px` } as React.CSSProperties}
			>
				<div ref={replyListRef}>
					{allReplies.length > 0 && (
						<RepliesToggler
							replies={allReplies}
							setVisibleReplies={setVisibleReplies}
							unreadReplies={unreadReplies}
							setReplyView={setReplyView}
							threadKey={threadKey}
							isAllRepliesView={currentReplyView === ViewReplyOptions.ALL}
							numOfHiddenReplies={numOfHiddenReplies}
							commentType={commentType}
						/>
					)}
					{visibleReplies.length > 0 && (
						<div
							css={[!fg('confluence_frontend_comments_panel_v2') && replyListStaticStraightBorders]}
							data-testid={`comment-thread-${parentComment.id}-reply-list`}
						>
							{visibleReplies.map(
								(reply) =>
									reply && (
										// eslint-disable-next-line jsx-a11y/no-static-element-interactions
										<div
											css={[
												fg('confluence_frontend_comments_panel_v2')
													? replyBoxStyles
													: oldReplyBoxStyles,
											]}
											key={reply.id}
											data-testid={`comment-thread-${parentComment.id}-replies-${reply.id}`}
											onMouseEnter={() => handleMouseEnter(reply.id)}
											onMouseLeave={handleMouseLeave}
										>
											<Comment
												comment={reply}
												supportedTopLevelActions={supportedTopLevelActions}
												pageInfo={pageInfo}
												pageMode={pageMode}
												isUnread={
													!commentsMarkedAsRead[commentType][threadKey]?.has(reply.id) &&
													reply.isUnread
												}
												isHovered={hoveredCommentId === reply.id}
												threadKey={threadKey}
											/>
										</div>
									),
							)}
						</div>
					)}
				</div>
				{expandReplyEditor && fg('confluence_frontend_comments_panel_v2')
					? renderCommentEditor()
					: renderReplyButton()}
			</div>

			{expandReplyEditor && !fg('confluence_frontend_comments_panel_v2') && renderCommentEditor()}
		</>
	);
};
